import * as React from 'react';
import AvatarEditorModal from 'components/AvatarEditorModal';
import WebcamAvatarModal from 'components/WebcamAvatarModal';
import ModalTrigger from 'components/ModalTrigger';
import MenuButton from 'components/MenuButton';
import MenuItem from 'components/MenuItem';
import Button from 'components/Button';
import Webcam from 'components/Webcam';
import Glyph from 'components/Glyph';
import { mountModal } from 'lib/modal';
import Menu from 'components/Menu';
import FileReader from 'components/FileReader';
import { FileReaderFile } from 'components/FileReader';

/**
 * ## Button triggers browser file explorer and passes the file to AvatarEditorModal
 *
 * When the button is clicked, it triggers file explorer from <input>. When a
 * file is selected, the onChange handler for <input> fires and loads the
 * file as a dataUrl string. This dataUrl is passed to the AvatarEditorModal
 * which can render it as an image.
 *
 * ```example
 * <AvatarEditorModalTriggerButton>
 *  Click here to upload file
 * </AvatarEditorModalTriggerButton>
 * ```
 */

type Props = {
  onSuccess: () => void;
  children?: React.ReactNode;
  fileContextKey: string;
  uploadRequest: (ctx: string, name: string, blob: File, onSuccess: () => void) => void;
  loadThumbnail: (fileContextKey: string, filename: string, thumbnail: string) => void;
};

export default class AvatarEditorModalTriggerButton extends React.Component<Props> {
  fileReader: FileReader | null = null;

  handleFileRead = (file: FileReaderFile) => {
    mountModal(
      <AvatarEditorModal
        image={file.dataURL}
        filename={file.name}
        onSuccess={this.props.onSuccess}
        loadThumbnail={this.props.loadThumbnail}
        uploadRequest={this.props.uploadRequest}
        fileContextKey={this.props.fileContextKey}
      />,
      this
    );
  };

  openFileExplorer = () => {
    if (this.fileReader) {
      this.fileReader.openExplorer();
    }
  };

  renderChoicesMenu() {
    const webcamModalMenuItem = (
      <ModalTrigger
        modal={
          <WebcamAvatarModal
            onSuccess={this.props.onSuccess}
            uploadRequest={this.props.uploadRequest}
            loadThumbnail={this.props.loadThumbnail}
            fileContextKey={this.props.fileContextKey}
          />
        }>
        <MenuItem menuKey={0}>
          <Glyph name="camera" size="medium" className="mrm" />
          Take picture from your webcam
        </MenuItem>
      </ModalTrigger>
    );

    const menu = (
      <Menu>
        {webcamModalMenuItem}
        <MenuItem menuKey={1} onClick={this.openFileExplorer}>
          <Glyph name="upload" size="medium" className="mrm" />
          Upload picture from your computer
        </MenuItem>
      </Menu>
    );

    return (
      <MenuButton level="icon" alignment="middle" menu={menu}>
        {this.props.children || null}
      </MenuButton>
    );
  }

  renderFilePicker() {
    const { children, ...rest } = this.props;

    return (
      <Button {...rest} onClick={this.openFileExplorer}>
        {children}
      </Button>
    );
  }

  render() {
    return (
      <div>
        <FileReader ref={p => (this.fileReader = p)} onRead={this.handleFileRead} />
        {Webcam.isSupported() ? this.renderChoicesMenu() : this.renderFilePicker()}
      </div>
    );
  }
}
